import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleDot, XCircle, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { useWorkspaceAuditImports, useDecideAuditImport } from "@/lib/auditImportQueries";
import { Textarea } from "@/components/ui/textarea";

const STATUS_LABELS = {
  pending_review: "Awaiting your review", accepted: "Accepted", partially_accepted: "Partially accepted", dismissed: "Dismissed",
};
const STATUS_COLORS = {
  pending_review: "bg-amber-100 text-amber-800", accepted: "bg-emerald-100 text-emerald-800",
  partially_accepted: "bg-blue-100 text-blue-800", dismissed: "bg-ink/10 text-ink-soft",
};
const EXERCISE_RESOLUTION_LABELS = {
  matched: "Mapped to a Library exercise", no_suitable_exercise: "No suitable exercise for this focus", unmapped_code: "Legacy exercise code not yet mapped",
};

function ReportPreview({ report }) {
  if (!report) return <p className="text-xs text-ink-soft">No report content in this import's findings snapshot.</p>;
  return (
    <div className="mt-3 space-y-3" data-testid="audit-import-report-sections">
      <p className="text-xs font-medium text-ink-soft uppercase tracking-wide">{report.emailSubject}</p>
      {(report.sections || []).map((s) => (
        <div key={s.sectionId} data-testid={`audit-import-report-section-${s.sectionId}`}>
          <p className="text-sm font-medium text-ink">{s.heading}</p>
          <p className="text-sm text-ink-soft mt-0.5 whitespace-pre-wrap">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function AuditImportRow({ workspaceId, item }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const decide = useDecideAuditImport(workspaceId);
  const findings = item.findings_snapshot || {};
  const exerciseMatch = findings.exercise_match || {};

  async function handleDecision(decision) {
    try {
      await decide.mutateAsync({ waiId: item.wai_id, decision, note: note || null });
      toast.success(
        decision === "accepted" ? "Audit import accepted — the client can now see their Audit Result Report"
          : decision === "partially_accepted" ? "Audit import partially accepted — the client can now see their Audit Result Report"
          : "Audit import dismissed"
      );
    } catch (err) {
      toast.error("Couldn't record that decision");
    }
  }

  return (
    <div className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`audit-import-row-${item.wai_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs text-ink-soft font-mono" data-testid={`audit-import-audit-id-${item.wai_id}`}>Audit: {item.audit_id}</p>
          <p className="text-xs text-ink-soft font-mono">Import: {item.wai_id}</p>
          <p className="text-xs text-ink-soft mt-1">
            Completed {new Date(item.audit_completed_at).toLocaleDateString()} · Imported {new Date(item.imported_at).toLocaleDateString()}
          </p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[item.status] || "bg-ink/10 text-ink-soft"}`} data-testid={`audit-import-status-${item.wai_id}`}>
          {STATUS_LABELS[item.status] || item.status}
        </span>
      </div>

      <div className="mt-2 text-sm text-ink-soft space-y-1">
        <p><span className="text-ink font-medium">Focus:</span> {exerciseMatch.focusCategoryId || "Not determined"}</p>
        <p>
          <span className="text-ink font-medium">Exercise:</span> {EXERCISE_RESOLUTION_LABELS[item.exercise_resolution_status] || item.exercise_resolution_status}
          {item.mapping_version != null && <span className="text-xs text-ink-soft"> (mapping v{item.mapping_version})</span>}
        </p>
      </div>

      {item.status !== "pending_review" && (
        <p className="text-xs text-ink-soft mt-2" data-testid={`audit-import-decision-note-${item.wai_id}`}>
          Decided {new Date(item.decided_at).toLocaleDateString()}{item.decision_note ? ` — "${item.decision_note}"` : ""}
        </p>
      )}

      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button onClick={() => setExpanded((e) => !e)} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid={`audit-import-toggle-report-${item.wai_id}`}>
          <FileText size={14} /> {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expanded ? "Hide" : "View"} full report
        </button>
      </div>
      {expanded && <ReportPreview report={findings.report} />}

      {item.status === "pending_review" && (
        <div className="mt-3 pt-3 border-t border-ink/10">
          <Textarea
            value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for your own record…"
            className="text-xs min-h-[60px] mb-2" data-testid={`audit-import-note-${item.wai_id}`}
          />
          <div className="flex items-center gap-3">
            <button onClick={() => handleDecision("accepted")} disabled={decide.isPending} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50" data-testid={`audit-import-accept-${item.wai_id}`}>
              <CheckCircle2 size={14} /> Accept
            </button>
            <button onClick={() => handleDecision("partially_accepted")} disabled={decide.isPending} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 disabled:opacity-50" data-testid={`audit-import-partially-accept-${item.wai_id}`}>
              <CircleDot size={14} /> Partially accept
            </button>
            <button onClick={() => handleDecision("dismissed")} disabled={decide.isPending} className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50" data-testid={`audit-import-dismiss-${item.wai_id}`}>
              <XCircle size={14} /> Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Master v5 Prompt 29 — Client 360's Audit Result tab: every
 * WorkspaceAuditImport for this exact member, coach-reviewable
 * (accept/partially accept/dismiss). AuditReport + WorkspaceAuditImport
 * stay the sole source of truth (Prompt 27) — deciding here never
 * touches Recommendation/WorkspaceLayerState/Commitment/PresenceFocus. */
export default function AuditResultTab({ workspaceId, memberId }) {
  const { data, isPending, isError } = useWorkspaceAuditImports(workspaceId);

  if (isPending) return <p className="text-sm text-ink-soft" data-testid="client-360-audit-result-loading">Loading Audit imports…</p>;
  if (isError) return <p className="text-sm text-red-600" data-testid="client-360-audit-result-error">Couldn't load Audit imports.</p>;

  const items = (data.items || []).filter((i) => i.owner_member_id === memberId);

  if (items.length === 0) {
    return (
      <p className="text-sm text-ink-soft flex items-center gap-2" data-testid="client-360-audit-result-empty">
        <FileText size={16} /> No High-Stakes Audit has been imported for this client yet.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="client-360-audit-result-tab">
      {items.map((item) => (
        <AuditImportRow key={item.wai_id} workspaceId={workspaceId} item={item} />
      ))}
    </div>
  );
}
